import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSession } from '../lib/session';
import { fetchWelcomeVideo, markWelcomeSeen } from '../lib/welcome';
import { VideoPlayer } from '../components/VideoPlayer';
import { Heading, Text, Stack, Button } from '../components/ui';
import './Welcome.css';

/**
 * One-time founding-member welcome screen. Shown right after onboarding on a
 * member's first visit (App.tsx gate: is_founding_member && !seen_welcome).
 * Plays a short team-uploaded landscape video, then drops them into the app.
 *
 * Dismissing flips profiles.seen_welcome (so it never shows again) and does a
 * full-page nav to '/' so the session re-reads the flag.
 */
export default function Welcome() {
  const { profile } = useSession();
  const [mediaId, setMediaId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [continuing, setContinuing] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      const { media_id } = await fetchWelcomeVideo(ctrl.signal);
      if (!ctrl.signal.aborted) {
        setMediaId(media_id);
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  // Staff preview (?preview=1): don't flip the "seen" flag, just leave.
  const preview = new URLSearchParams(useLocation().search).get('preview') === '1';

  async function handleContinue() {
    if (preview) { window.location.assign('/'); return; }
    setContinuing(true);
    try {
      await markWelcomeSeen();
    } catch (err) {
      // Non-fatal: worst case they see it once more. Don't block entry.
      console.warn('[welcome] mark seen failed', err);
    }
    // Full-page nav so SessionProvider re-reads seen_welcome and the gate clears.
    window.location.assign('/');
  }

  const firstName = (profile?.display_name ?? '').trim().split(/\s+/)[0] || null;

  return (
    <div data-ds className="welcome grid min-h-[100dvh] place-items-center bg-bg p-6">
      <Stack gap="loose" className="welcome__card w-full max-w-[720px] items-center text-center">
        <p className="kicker welcome__kicker">Founding member</p>
        <Heading level="h1">
          {firstName ? `Welcome, ${firstName}.` : 'Welcome to the Converted Closet.'}
        </Heading>
        <Text role="lede" muted prose className="text-center">
          Congratulations! You're one of the first through the door. Please take a
          moment to watch the welcome video below.
        </Text>

        {loading ? (
          <div className="welcome__video aspect-video bg-bone" aria-hidden="true" />
        ) : mediaId ? (
          <div className="welcome__video">
            <VideoPlayer mediaId={mediaId} aspect={16 / 9} />
          </div>
        ) : (
          <Text role="body" muted prose className="text-center">
            We're so glad you're here. Take a look around; your closet awaits.
          </Text>
        )}

        <Button
          type="button"
          variant="primary"
          className="mt-3 min-w-[12rem]"
          onClick={handleContinue}
          disabled={continuing}
        >
          {continuing ? 'Taking you in...' : 'Enter the Closet'}
        </Button>
      </Stack>
    </div>
  );
}
